import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { AccountInfo, EventMessage, EventType } from '@azure/msal-browser';
import { AsyncSubject, BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

/**
 * MSAL-backed authentication service.
 *
 * Preserves the legacy public surface (`currentUser`, `authenticated`,
 * `signinRedirect`, `signout`, `canActivate`) so existing consumers keep
 * working while the underlying provider is now Microsoft MSAL.
 *
 * Route protection should prefer `MsalGuard`, and outgoing API calls should
 * be authenticated via `MsalInterceptor` — both wired up in `main.ts`.
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  /** Currently signed-in MSAL account (null when signed out). */
  public currentUser: BehaviorSubject<AccountInfo | null> = new BehaviorSubject<AccountInfo | null>(null);

  /** Emits once after initial MSAL interaction (redirect handling) settles. */
  public authenticated = new AsyncSubject<boolean>();

  constructor(
    private msalService: MsalService,
    private msalBroadcastService: MsalBroadcastService,
  ) {
    // MSAL is initialized and the redirect response is handled by an
    // APP_INITIALIZER in main.ts BEFORE this service is constructed, so we
    // only need to mirror MSAL state into the legacy observables here.

    // Seed initial state from any account already cached by MSAL.
    const initialAccount = this.pickActiveAccount();
    this.currentUser.next(initialAccount);
    this.authenticated.next(!!initialAccount);
    this.authenticated.complete();

    // Keep currentUser in sync with login/logout/token events.
    //
    // NB: do NOT include ACTIVE_ACCOUNT_CHANGED here — that event is fired
    // by `setActiveAccount()`, which `pickActiveAccount()` calls, which would
    // re-enter this subscription and blow the stack.
    this.msalBroadcastService.msalSubject$
      .pipe(
        filter(
          (msg: EventMessage) =>
            msg.eventType === EventType.LOGIN_SUCCESS ||
            msg.eventType === EventType.ACQUIRE_TOKEN_SUCCESS ||
            msg.eventType === EventType.LOGOUT_SUCCESS,
        ),
      )
      .subscribe(() => {
        this.currentUser.next(this.pickActiveAccount());
      });
  }

  private pickActiveAccount(): AccountInfo | null {
    const account =
      this.msalService.instance.getActiveAccount() ??
      this.msalService.instance.getAllAccounts()[0] ??
      null;
    if (account) {
      this.msalService.instance.setActiveAccount(account);
    }
    return account;
  }

  public signout(): void {
    this.msalService.logoutRedirect();
  }

  public signinRedirect(redirectTo?: string, signup = false): void {
    const extraQueryParameters: Record<string, string> = {};
    if (signup) {
      extraQueryParameters.showSignup = '1';
    }
    this.msalService.loginRedirect({
      scopes: [],
      redirectStartPage: redirectTo,
      extraQueryParameters,
    });
  }

  public get loggedIn(): boolean {
    return !!this.currentUser.value;
  }

  /**
   * Legacy guard kept for backward compatibility. New routes should use
   * `MsalGuard` directly.
   */
  canActivate(_route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    this.authenticated.subscribe((authenticated) => {
      if (!authenticated) {
        this.signinRedirect(state.url);
      }
    });
    return this.authenticated;
  }
}
